import { Button, Stack, Typography, useTheme } from "@mui/material";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { AdvisorContactLine } from "web_myplann/Advisor/Components/AdvisorContactLine";
import { AdvisorMainBadge } from "web_myplann/Advisor/Components/AdvisorMainBadge";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InitialsAvatar } from "web_myplann/Shared/Components/InitialsAvatar";
import { handlePhoneClick } from "web_myplann/Shared/Utils/openExternalApps";
import { device } from "web_myplann/Utils/device";

const R = Resources.Advisor;

type Props = {
	id: string;
	name: string;
	job?: string;
	phone?: string;
	email?: string;
	address?: string;
	photo?: string;
	conversationId?: number;
	showMainBadge?: boolean;
};

export const AdvisorCard: FC<Props> = ({
	id,
	name,
	job,
	phone,
	email,
	address,
	photo,
	conversationId,
	showMainBadge,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const hasGoalsRight = useGoalsRightOptional();

	const openChat = () => {
		if (hasGoalsRight) {
			navigate(
				conversationId
					? Routing.Chat.Detail.path(String(conversationId))
					: Routing.Chat.Detail.path("new", id),
			);
			return;
		}
		device.navigateChatRequest(String(conversationId ?? ""), name, id);
	};

	return (
		<Stack
			bgcolor={theme.palette.background.paper}
			px={1.6}
			pt={2}
			pb={2.4}
			alignItems="center"
		>
			<InitialsAvatar name={name} photo={photo} mb={1.2} />

			<Stack direction="row" alignItems="center" gap={0.8}>
				<Typography variant="h2">{name}</Typography>
				{showMainBadge && <AdvisorMainBadge />}
			</Stack>

			{job && (
				<Typography variant="body2" color={theme.palette.grey[500]} mt={0.2}>
					{job}
				</Typography>
			)}

			<Stack gap={0.8} alignItems="center" mt={1.6} mb={2}>
				{phone && <AdvisorContactLine icon={Phone} text={phone} />}
				{email && <AdvisorContactLine icon={Mail} text={email} />}
				{address && <AdvisorContactLine icon={MapPin} text={address} />}
			</Stack>

			<Stack direction="row" gap={0.8} width="100%">
				{phone && (
					<Button
						variant="primary"
						startIcon={<Phone size={theme.iconSize.sm} />}
						onClick={() => handlePhoneClick(phone)}
						sx={{ flex: 1 }}
					>
						{t(R.call)}
					</Button>
				)}
				<Button
					variant="tertiary"
					startIcon={<MessageCircle size={theme.iconSize.sm} />}
					onClick={openChat}
					sx={{ flex: 1 }}
				>
					{t(R.messages)}
				</Button>
			</Stack>
		</Stack>
	);
};
